import { useState } from 'react'
import { GeneralOperation } from '~/hooks/toast/eventHandlers/generalToastEventHandler'
import { useEmitterService } from '~/hooks/useEmitterService'
import {
  NotificationDisposition,
  NotificationDomain,
  NotificationEmitterEventType,
} from '~/services/EventEmitter/types/NotificationEmitterTypes'

import { copyToClipboard as copyToClipboardUtil } from '@mangovoice/mango-ui'

export function useClipboard(
  args: {
    timeout?: number
    onSuccess?: () => void
    onFailure?: (reason?: string) => void
  } = {},
) {
  const { emit } = useEmitterService()

  const handleSuccess = () => {
    emit(NotificationEmitterEventType.PROCESS_UPDATED, {
      disposition: NotificationDisposition.Success,
      domain: NotificationDomain.GENERAL,
      operation: GeneralOperation.GeneralCopyToClipboard,
    })
  }

  const handleFailure = (reason?: any) => {
    console.error(reason)
    emit(NotificationEmitterEventType.PROCESS_UPDATED, {
      disposition: NotificationDisposition.Error,
      domain: NotificationDomain.GENERAL,
      operation: GeneralOperation.GeneralCopyToClipboard,
    })
  }

  const {
    timeout = 2000,
    onSuccess = handleSuccess,
    onFailure = handleFailure,
  } = args
  const [error, setError] = useState<Error | null>(null)
  const [hasCopied, setHasCopied] = useState(false)
  const [copyTimeout, setCopyTimeout] = useState<number | null>(null)

  const handleCopyResult = (value: boolean) => {
    window.clearTimeout(copyTimeout!)
    setCopyTimeout(window.setTimeout(() => setHasCopied(false), timeout))
    setHasCopied(value)
  }

  const copyToClipboard = (
    valueToCopy: string,
    onSuccessOverride?: () => void,
    onFailureOverride?: (reason?: string) => void,
  ) => {
    // Electron, iframe, and insecure-context fallbacks live in the shared
    // util so custom-look buttons (FAB, menu items, clickable text) get the
    // same clipboard behavior as CopyButton.
    copyToClipboardUtil(valueToCopy ?? '')
      .then(() => {
        handleCopyResult(true)
        if (onSuccessOverride) {
          onSuccessOverride()
        } else {
          onSuccess()
        }
      })
      .catch((reason: unknown) => {
        setError(reason instanceof Error ? reason : new Error(String(reason)))
        if (onFailureOverride) {
          onFailureOverride()
        } else {
          onFailure(String(reason ?? 'Failed to copy to clipboard'))
        }
      })
  }

  const doReset = () => {
    setHasCopied(false)
    setError(null)
    window.clearTimeout(copyTimeout!)
  }

  return { copyToClipboard, doReset, error, hasCopied }
}
